import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { copyToClipboard } from "util/browser";

/** Copied from https://raw.githubusercontent.com/jpalumickas/use-window-focus/main/src/index.ts, used under MIT license */

const hasFocus = () => typeof document !== "undefined" && document.hasFocus();

export const useWindowFocus = () => {
    const [focused, setFocused] = useState(hasFocus); // Focus for first render

    useEffect(() => {
        setFocused(hasFocus()); // Focus for additional renders

        const onFocus = () => setFocused(true);
        const onBlur = () => setFocused(false);

        window.addEventListener("focus", onFocus);
        window.addEventListener("blur", onBlur);

        return () => {
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("blur", onBlur);
        };
    }, []);

    return focused;
};

export const useCopyToClipboard = () => {
    const [copied, setCopied] = useState(false);
    const copy = (text: string) => {
        const result = copyToClipboard(text);
        if (result) {
            setCopied(true);
        }
    };
    useEffect(() => {
        if (copied) {
            const timeout = setTimeout(() => setCopied(false), 1000);
            return () => clearTimeout(timeout);
        }

        return () => {};
    }, [copied, setCopied]);
    return [copy, copied] as const;
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- generic constraint
export const useDebouncedState = <T extends {}>(
    input: T,
    delay: number | ((value: T) => number) = 600,
): [T, Dispatch<SetStateAction<T>>, T] => {
    const [value, setValue] = useState<T>(input);
    const [debouncedValue, setDebouncedValue] = useState<T>(input);
    useEffect(() => {
        const resolvedDelay = typeof delay === "function" ? delay(value) : delay;
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, resolvedDelay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return [value, setValue, debouncedValue];
};

/** Copied from From https://github.com/mantinedev/mantine, used under MIT license */

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
export function useDebouncedValue<T = any>(
    value: T,
    wait: number,
    options = { leading: false },
) {
    const [_value, setValue] = useState(value);
    const mountedRef = useRef(false);
    const timeoutRef = useRef<number>(null);
    const cooldownRef = useRef(false);

    const cancel = () => window.clearTimeout(timeoutRef.current);

    useEffect(() => {
        if (mountedRef.current) {
            if (!cooldownRef.current && options.leading) {
                cooldownRef.current = true;
                setValue(value);
            } else {
                cancel();
                timeoutRef.current = window.setTimeout(() => {
                    cooldownRef.current = false;
                    setValue(value);
                }, wait);
            }
        }
    }, [value, options.leading]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        mountedRef.current = true;
        return cancel;
    }, []);

    return [_value, cancel] as const;
}

export const useQueryParams = () => {
    const urlParams = new URLSearchParams(useLocation().search);
    const params: { [key: string]: string } = {};
    urlParams.forEach((value, key) => {
        params[key] = value;
    });
    return params;
};
